import { PropsWithChildren, useCallback, useMemo, useState } from 'react';

import { CommandContext } from '@components/features/CommandMenu/context';
import { CommandMenu } from '@components/features/CommandMenu/Menu';
import { Command } from '@components/features/CommandMenu/types';

export function CommandProvider({ children }: PropsWithChildren) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState<string[]>([]);
  const [commands, setCommands] = useState<Record<string, Command>>({});

  const register = useCallback((command: Command) => {
    setCommands((prev) => ({ ...prev, [command.id]: command }));
  }, []);

  const unregister = useCallback((command: Command) => {
    setCommands((prev) => {
      const next = { ...prev };
      delete next[command.id];
      return next;
    });
  }, []);

  const openMenu = useCallback(() => {
    setOpen(true);
  }, []);

  const startAsyncLoad = useCallback(() => {
    const id = crypto.randomUUID();
    setLoading((prev) => [...prev, id]);
    return () => {
      setLoading((prev) => prev.filter((id) => id !== id));
    };
  }, []);

  const context = useMemo(
    () => ({
      open: openMenu,
      isOpen: open,
      startAsyncLoad,
      query,
      setQuery,
      register,
      unregister,
    }),
    [open, openMenu, startAsyncLoad, query, setQuery, register, unregister]
  );

  return (
    <CommandContext.Provider value={context}>
      <CommandMenu
        open={open}
        onOpenChange={setOpen}
        search={query}
        onSearchChange={setQuery}
        isLoading={loading.length > 0}
        commands={commands}
      />
      {children}
    </CommandContext.Provider>
  );
}
